import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Alert, Box, Stack, Switch, Typography } from '@mui/material';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
} from '../api/notificationsApi';
import { colorTokens, typographyTokens } from '../app/designTokens';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { Button } from '../components/ui/Button';

const { semantic } = colorTokens;

const channelKeys = ['in_app', 'email', 'sms', 'push'];
const futureChannels = ['email', 'sms', 'push'];

function PreferenceSwitch({ checked, disabled, onChange }) {
  return (
    <Switch
      size="small"
      checked={checked}
      disabled={disabled}
      onChange={onChange}
      inputProps={{ 'aria-label': 'toggle' }}
      sx={{
        '& .MuiSwitch-switchBase': {
          color: semantic.text.muted,
        },
        '& .MuiSwitch-switchBase.Mui-checked': {
          color: semantic.text.primary,
        },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
          bgcolor: semantic.status.brand.border,
          opacity: 1,
        },
        '& .MuiSwitch-track': {
          bgcolor: semantic.border.default,
          opacity: 1,
        },
        '& .MuiSwitch-switchBase.Mui-disabled': {
          color: semantic.text.disabled,
        },
        '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
          bgcolor: semantic.border.subtle,
          opacity: 1,
        },
      }}
    />
  );
}

export function NotificationPreferencesPage() {
  const { t } = useTranslation(['notifications', 'common']);
  const location = useLocation();
  const isPortal = location.pathname.startsWith('/portal');
  const notificationsPath = isPortal ? '/portal/notifications' : '/notifications';

  const [preferences, setPreferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  const categories = useMemo(
    () => [...new Set(preferences.map((item) => item.category))],
    [preferences],
  );

  const loadPreferences = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getNotificationPreferences();
      setPreferences(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreferences();
  }, [loadPreferences]);

  const getPreference = (category, channel) => preferences.find(
    (item) => item.category === category && item.channel === channel,
  );

  const handleToggle = async (category, channel, enabled) => {
    const next = preferences.map((item) => {
      if (item.category === category && item.channel === channel) {
        return { ...item, enabled };
      }

      return item;
    });

    setPreferences(next);
    setSaving(true);
    setMessage('');

    try {
      const updated = await updateNotificationPreferences({ preferences: next });
      setPreferences(updated);
      setMessage(t('notifications:preferences.saved'));
    } catch (err) {
      setError(err);
      await loadPreferences();
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'category',
        label: t('notifications:inbox.category'),
        render: (row) => (
          <Stack spacing={0.25} sx={{ minWidth: 0, py: 0.25 }}>
            <Typography
              sx={{
                fontFamily: typographyTokens.fontFamily,
                fontWeight: typographyTokens.fontWeightSemibold,
                fontSize: 13,
                lineHeight: '18px',
                color: semantic.text.primary,
              }}
            >
              {t(`notifications:preferences.categories.${row.category}`, {
                defaultValue: row.category,
              })}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: 11,
                lineHeight: '16px',
                color: semantic.text.muted,
              }}
            >
              {row.category}
            </Typography>
          </Stack>
        ),
      },
      ...channelKeys.map((channel) => ({
        id: channel,
        label: t(`notifications:preferences.channels_labels.${channel}`),
        align: 'center',
        render: (row) => {
          const isFuture = futureChannels.includes(channel);
          if (isFuture) {
            return (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <StatusBadge
                  status="coming_soon"
                  category="generic"
                  apiLabel={t('notifications:preferences.coming_soon')}
                  size="Sm"
                  tone="Neutral"
                />
              </Box>
            );
          }

          const pref = getPreference(row.category, channel);
          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <PreferenceSwitch
                checked={Boolean(pref?.enabled)}
                disabled={saving}
                onChange={(e) => handleToggle(row.category, channel, e.target.checked)}
              />
            </Box>
          );
        },
      })),
    ],
    // getPreference/handleToggle close over preferences/saving; columns must refresh with them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [preferences, saving, t],
  );

  const rows = useMemo(
    () => categories.map((category) => ({ id: category, category })),
    [categories],
  );

  const errorMessage = error?.response?.data?.message
    || error?.message
    || t('common:error');

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="Detail"
        title={t('notifications:preferences.title')}
        subtitle={t('notifications:preferences.subtitle')}
        backTo={notificationsPath}
      />

      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {loading ? (
        <LoadingSkeleton type="Table" rows={5} columns={5} />
      ) : error && preferences.length === 0 ? (
        <SectionCard variant="Danger" sx={{ mb: 0 }}>
          <Alert
            severity="error"
            sx={{ bgcolor: 'transparent', p: 0 }}
            action={(
              <Button variant="Ghost" onClick={loadPreferences} sx={{ minHeight: 32 }}>
                {t('common:retry')}
              </Button>
            )}
          >
            {errorMessage}
          </Alert>
        </SectionCard>
      ) : (
        <SectionCard
          variant="Default"
          title={t('notifications:preferences.channels')}
          noPadding
          sx={{ mb: 0 }}
        >
          {error && (
            <Alert severity="error" sx={{ mx: 2.5, mt: 2, mb: 0 }}>
              {errorMessage}
            </Alert>
          )}
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={rows}
            getRowKey={(row) => row.category}
            emptyMessage={t('common:no_data_found')}
            sx={{
              overflowX: 'auto',
              '& table': {
                minWidth: '100%',
              },
            }}
          />
        </SectionCard>
      )}
    </Box>
  );
}
